import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash-es/isEqual';
import {
  Form,
  UploadedDocument,
  FormPage,
  PageStatus,
  DocumentTypes,
  AustralianState,
  FormConfigType,
} from '@urbanx/agx-ui-components';
import type { ContractExchangePdfAlterationsSavedEvent, PdfProcessedEvent } from './types';
import { FormType } from '@urbanx/agx.campaigns.client';

interface FormState {
  selectedForm?: {
    formType: FormType;
    state: AustralianState;
    formConfigType: FormConfigType;
  };
  formId?: string;
  campaignId?: string;
  formName?: string;
  formPages?: FormPage[];
  formValues: any;
  formErrors: string[];
  pageStatuses: PageStatus[];
  generatedFormDocuments: UploadedDocument[];
  initialGeneratedFormDocuments: UploadedDocument[];
  generatedFormDocumentTransitionPending: boolean;
  generatedDocumentTypes: DocumentTypes[];
  downloadDocumentTypes?: DocumentTypes[];
  address?: Form['address'];
  propertyDetails?: Form['propertyDetails'];
  propertyImageUrls?: Form['propertyImageUrls'];
}

const initialState = {
  selectedForm: undefined,
  formId: undefined,
  campaignId: undefined,
  formName: undefined,
  formPages: undefined,
  formValues: {},
  formErrors: [],
  pageStatuses: [],
  generatedFormDocuments: [],
  initialGeneratedFormDocuments: [],
  generatedFormDocumentTransitionPending: false,
  generatedDocumentTypes: [],
  address: undefined,
  propertyDetails: undefined,
  propertyImageUrls: undefined,
} satisfies FormState as FormState;

const slice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    updateFormValue: (
      state,
      action: PayloadAction<{
        id: string;
        value?: any;
        parentPathIds?: string[];
      }>
    ) => {
      const { id, value, parentPathIds = [] } = action.payload;

      const pathToElement = [...parentPathIds, id];
      const existingValue = getExistingValue(pathToElement, state.formValues);

      if (isEqual(existingValue, value)) {
        return state;
      }

      return {
        ...state,
        formValues: applyValueToPath(pathToElement, state.formValues, value),
      };
    },
    updatePageStatuses: (state, action: PayloadAction<{ pageStatuses: PageStatus[] }>) => {
      const { pageStatuses = [] } = action.payload;

      return {
        ...state,
        pageStatuses,
      };
    },
    updateFormErrors: (state, action: PayloadAction<string[]>) => {
      const errors = action.payload;

      return {
        ...state,
        formErrors: errors,
      };
    },
    deleteFormErrorIds: (state) => {
      return {
        ...state,
        formErrors: [],
      };
    },
    setSelectedForm: (
      state,
      action: PayloadAction<{
        formType: FormType;
        state: AustralianState;
        formConfigType: FormConfigType;
      }>
    ) => {
      const { formType, state: ausState, formConfigType } = action.payload;

      return {
        ...state,
        selectedForm: {
          formType,
          state: ausState,
          formConfigType,
        },
      };
    },
    loadFormConfig: (state, action: PayloadAction<Form>) => {
      const {
        formType,
        formConfigType,
        state: AusState,
        formId,
        campaignId,
        name,
        pages,
        existingData,
        pageStatuses,
        address,
        propertyDetails,
        propertyImageUrls,
        documents,
        generatedDocumentTypes,
        downloadDocumentTypes,
      } = action.payload;

      if (!formId || !campaignId) {
        return state;
      }

      return {
        ...state,
        selectedForm: {
          formType,
          state: AusState,
          formConfigType,
        },
        formId,
        campaignId,
        formName: name,
        formPages: pages,
        formValues: existingData,
        pageStatuses,
        address: address,
        propertyDetails: propertyDetails,
        propertyImageUrls: propertyImageUrls,
        generatedFormDocuments: getActiveGeneratedDocuments(documents ?? [], existingData),
        initialGeneratedFormDocuments: documents ?? [],
        generatedFormDocumentTransitionPending: false,
        generatedDocumentTypes: generatedDocumentTypes ?? [],
        downloadDocumentTypes: downloadDocumentTypes,
      };
    },
    clearForm: (state) => {
      return {
        ...state,
        ...initialState,
      };
    },
    setGeneratedFormDocuments: (state, action: PayloadAction<PdfProcessedEvent>) => {
      const { campaignId, formId, uploadedDocument } = action.payload;

      if (state.campaignId !== campaignId || state.formId !== formId) {
        return state;
      }

      return {
        ...state,
        generatedFormDocumentTransitionPending: true,
        formValues:
          uploadedDocument.documentType === DocumentTypes.ContractExchange
            ? {
                ...state.formValues,
                ContractPdfInitialDocument: uploadedDocument,
              }
            : state.formValues,
        generatedFormDocuments: [
          ...state.generatedFormDocuments.filter(
            (doc) => doc.documentType !== uploadedDocument.documentType
          ),
          uploadedDocument,
        ],
      };
    },
    saveContractExchangePdfAlterations: (
      state,
      action: PayloadAction<ContractExchangePdfAlterationsSavedEvent>
    ) => {
      const { campaignId, formId, uploadedDocument, alteredUploadedDocument, editState } =
        action.payload;

      if (state.campaignId !== campaignId || state.formId !== formId) {
        return state;
      }

      return {
        ...state,
        formValues: {
          ...state.formValues,
          ContractPdfAlterations: editState,
          ContractPdfInitialDocument: uploadedDocument,
          ContractPdfAlteredDocument: alteredUploadedDocument,
        },
        generatedFormDocumentTransitionPending: false,
        generatedFormDocuments: [
          ...state.generatedFormDocuments.filter(
            (doc) => doc.documentType !== alteredUploadedDocument.documentType
          ),
          alteredUploadedDocument,
        ],
      };
    },
    clearGeneratedFormDocuments: (state) => {
      return {
        ...state,
        generatedFormDocumentTransitionPending: false,
        generatedFormDocuments: state.initialGeneratedFormDocuments.filter(
          (document) =>
            !document.documentType || !state.generatedDocumentTypes.includes(document.documentType)
        ),
        formValues: {
          ...state.formValues,
          ContractPdfInitialDocument: undefined,
          ContractPdfAlteredDocument: undefined,
        },
      };
    },
  },
});

const getExistingValue = (pathIds: any, state: any): any => {
  const currentPathIds = [...pathIds];
  const currentPathId = currentPathIds.shift();

  if (currentPathIds.length === 0) {
    return state != null ? state[currentPathId] : null;
  }

  if (state && state[currentPathId] != null) {
    return getExistingValue(currentPathIds, state[currentPathId]);
  }

  return null;
};

const applyValueToPath = (pathIds: any, state: any, value: string): Record<string, any> => {
  const remainingPathIds = [...pathIds];
  const currentPathId = remainingPathIds.shift();

  if (state == null) {
    state = {};
  }

  if (remainingPathIds.length === 0) {
    return {
      ...state,
      [currentPathId]: value,
    };
  }

  return {
    ...state,
    [currentPathId]: applyValueToPath(remainingPathIds, state[currentPathId], value),
  };
};

const getActiveGeneratedDocuments = (documents: UploadedDocument[], formValues: any) => {
  const contractDocument =
    formValues?.ContractPdfAlteredDocument ?? formValues?.ContractPdfInitialDocument;

  if (!contractDocument) return documents;

  return [
    ...documents.filter((document) => document.documentType !== DocumentTypes.ContractExchange),
    contractDocument,
  ];
};

export default slice.reducer;

const {
  updateFormValue,
  updatePageStatuses,
  updateFormErrors,
  deleteFormErrorIds,
  setSelectedForm,
  loadFormConfig,
  clearForm,
  setGeneratedFormDocuments,
  saveContractExchangePdfAlterations,
  clearGeneratedFormDocuments,
} = slice.actions;

export {
  updateFormValue,
  updatePageStatuses,
  updateFormErrors,
  deleteFormErrorIds,
  setSelectedForm,
  loadFormConfig,
  clearForm,
  setGeneratedFormDocuments,
  saveContractExchangePdfAlterations,
  getExistingValue,
  clearGeneratedFormDocuments,
};
